import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DocumentCreateModel, DocumentModel } from '@data-layer/models/documents';
import { ValidationMessageModel } from '@data-layer/models/common';
import {
  MISSING_ATTACHED_DOCUMENT_BILL_OF_LADING,
  MISSING_ATTACHED_DOCUMENT_INVOICE,
  MISSING_ATTACHED_DOCUMENT_POA,
} from '@constants/entity-validation-messages';
import { enumDocumentState, enumDocumentType } from '@data-layer/enums/documents';
import { enumUserBetaFlag } from '@data-layer/enums/user';
import { UserBetaFlagModel } from '@data-layer/models/user';
import { Observable } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { IEsDocumentStateUpdate } from '../../interfaces';
import { POA_FILE_URL } from '@constants/constants';
import { CompaniesService } from '@services/companies.service';

@Component({
  selector: 'es-poa-document-download',
  templateUrl: './es-poa-document-download.component.html',
  styleUrls: ['./es-poa-document-download.component.scss'],
})
export class EsPoaDocumentDownloadComponent {
  @Input() public document: DocumentModel;
  @Input() public poaFileUrl: string = POA_FILE_URL;
  @Input() public isOverrideDefaultRequired: boolean;
  @Input() public isCanUploadDocuments: boolean;
  @Input() public isCanRemoveDocuments: boolean = false;
  @Input() public validationMessages: ValidationMessageModel[] = [];
  @Input() public attachmentTypeName: string;
  @Input() public attachmentName: string;
  @Input() public attachmentHint: string;
  @Input() public openPoaSidebar: () => void;
  @Input() public managePoaFooterHint: string = 'COMPANY.MANAGE_POA_FROM_SIDEBAR';

  @Input() public upload: (document: DocumentCreateModel, file: File, uploadToCompany: boolean) => Observable<any>;
  @Input() public remove: (documentId: number) => Observable<any>;
  @Output() public stateUpdate: EventEmitter<IEsDocumentStateUpdate> = new EventEmitter<IEsDocumentStateUpdate>();

  public isLoading: boolean = false;

  constructor(private companiesService: CompaniesService) {}

  private readonly managePoaFooterAction = () => this.onManagePoaClick();

  public get footerHintAction(): (() => void) | null {
    return this.openPoaSidebar ? this.managePoaFooterAction : null;
  }

  public get hasPoaDocusignFlag(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.poaDocusign);
  }

  public get isRequiredDocument(): boolean {
    return this.isOverrideDefaultRequired
      ? this.isOverrideDefaultRequired
      : !!this.validationMessages.find(
          (message) =>
            ((message.text === MISSING_ATTACHED_DOCUMENT_INVOICE && this.document.type === enumDocumentType.INVOICE) ||
              (message.text === MISSING_ATTACHED_DOCUMENT_BILL_OF_LADING && this.document.type === enumDocumentType.BILL_OF_LADING) ||
              (message.text === MISSING_ATTACHED_DOCUMENT_POA && this.document.type === enumDocumentType.POWER_OF_ATTORNEY) ||
              (message.text === MISSING_ATTACHED_DOCUMENT_POA && this.document.type === enumDocumentType.POWER_OF_ATTORNEY_TRUCKER)) &&
            message.params &&
            message.params.relatedEntityId === this.document.relatedEntityId,
        );
  }

  public onStateUpdate(state: enumDocumentState) {
    this.stateUpdate.emit({ documentId: this.document.id, state });
  }

  public onRemoveFile(event: DocumentModel): void {
    if (!this.isCanRemoveDocuments || !this.remove) {
      return;
    }
    this.isLoading = true;
    this.remove(event.id)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
        take(1),
      )
      .subscribe();
  }

  public openPoaModal(file: File) {
    if (this.hasPoaDocusignFlag && this.openPoaSidebar) {
      this.openPoaSidebar();
      return;
    }
    if (!this.upload) {
      return;
    }
    const createDocument: DocumentModel = { ...this.document, fileName: file.name };
    this.isLoading = true;
    this.upload(createDocument, file, false)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
        take(1),
      )
      .subscribe(() => {});
  }

  public onManagePoaClick(): void {
    if (!this.openPoaSidebar) {
      return;
    }
    this.openPoaSidebar();
  }
}
